import {AsyncPipe, DecimalPipe} from '@angular/common';
import {AfterViewChecked, Component, effect, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {FormsModule} from '@angular/forms';

import {MatBadge} from '@angular/material/badge';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {MatDialog} from '@angular/material/dialog';
import {MatDivider} from "@angular/material/divider";
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from "@angular/material/menu";
import {MatSidenavContent, MatSidenavModule} from '@angular/material/sidenav';
import {MatToolbar, MatToolbarRow} from '@angular/material/toolbar';
import {MatTooltip} from '@angular/material/tooltip';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {Annotation, AnnotationService} from '@app/annotation/annotation.service';
import {Asset, AssetType, deserializeAsset, ReviewAsset, VideoAsset} from '@app/asset';
import {AuthService} from '@app/auth/auth.service';
import {PreferencesService} from "@app/preferences/preferences.service";
import {AnnotationTool, CompareRole, DeepLink, InvalidGuid, PresentingState, ReviewState, Slot, SyncState} from "@app/review/models";
import {IResizable} from '@app/review/resizable';
import {ReviewAnnotationListComponent} from '@app/review/review-annotation-list/review-annotation-list.component';
import {ReviewAssetInfoComponent} from "@app/review/review-asset-info/review-asset-info.component";
import {ReviewAssetItemComponent} from '@app/review/review-asset-item/review-asset-item.component';
import {ReviewImageComponent} from '@app/review/review-image/review-image.component';
import {ReviewTimelineComponent} from '@app/review/review-timeline/review-timeline.component';
import {PlaybackState, ReviewVideoService, Source} from '@app/review/review-video.service';
import {ReviewVideoComponent} from '@app/review/review-video/review-video.component';
import {ReviewService} from '@app/review/review.service';
import {StageService} from "@app/review/stage.service";
import {StageComponent} from "@app/review/stage/stage.component";
import {TimeRange} from '@app/review/timerange';
import {TimeScale} from '@app/review/timescale';
import {ConfirmDialogComponent} from '@app/shared/confirm-dialog/confirm-dialog.component';
import {IdenticonPipe} from '@app/shared/identicon.pipe';
import {User} from '@app/shared/user.service';
import {UploadButtonComponent} from '@app/upload/upload-button/upload-button.component';
import {UploadDropComponent} from '@app/upload/upload-drop/upload-drop.component';
import {UploadService} from '@app/upload/upload.service';
import {ConnectedState, MessageType, WebSocketService} from '@app/websocket/websocket.service';

import {firstOrDefault} from '@shared/array-extensions';
import {hexToRgb, intToHex, rgbToHex} from "@shared/color";

import {combineLatestWith, map, Observable, Subscription} from 'rxjs';
import {filter, startWith, take} from 'rxjs/operators';


class ReviewVM {
    review: ReviewAsset;
    reviewState: ReviewState;
    connectedState: ConnectedState;
    presenterState: PresentingState;
    user: User;
    playbackState: PlaybackState;
}

@Component({
    selector: 'frog-review',
    imports: [
        MatToolbar,
        MatToolbarRow,
        MatButton,
        AsyncPipe,
        FormsModule,
        MatSidenavModule,
        MatIconModule,
        ReviewAssetItemComponent,
        ReviewTimelineComponent,
        ReviewVideoComponent,
        IdenticonPipe,
        ReviewImageComponent,
        UploadDropComponent,
        UploadButtonComponent,
        RouterLink,
        MatTooltip,
        MatIconButton,
        ReviewAnnotationListComponent,
        MatBadge,
        StageComponent,
        MatButtonToggleGroup,
        MatButtonToggle,
        MatMenuModule,
        DecimalPipe,
        MatDivider,
        ReviewAssetInfoComponent,
    ],
    templateUrl: './review.component.html',
    styleUrl: './review.component.scss',
})
export class ReviewComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild(MatSidenavContent) sideNav: MatSidenavContent;
    @ViewChildren('assetViewer', { emitDistinctChangesOnly: true }) viewers: QueryList<IResizable>;

    public vm$: Observable<ReviewVM>;
    public annotations$: Observable<Annotation[]>;
    public comments$: Observable<Annotation[]>;
    public syncingState$: Observable<SyncState>;
    public presentingState$: Observable<PresentingState>;

    public asset: Asset;
    // The Compare slot's displayed Asset while comparing (null otherwise) - see
    // docs/design/review-compare-assets.md.
    public compareAsset: Asset;
    public mouseDown: boolean;
    public volume: number;
    public muted: boolean;
    public looping: boolean;
    public ConnectedState = ConnectedState;
    public SyncState = SyncState;
    public PresentingState = PresentingState;
    public AnnotationTool = AnnotationTool;
    public Slot = Slot;
    public shiftDown: boolean;
    public color: string;
    public size: number;
    public opacity: number;
    public tool: AnnotationTool;
    public displayInfo: boolean;

    private _connectionStateSubscription: Subscription;
    private _deepLink: DeepLink;
    // Set by applyAsset()/applyCompareAsset() whenever a displayed asset changes; consumed on the next
    // ngAfterViewChecked so resizeHandler() runs once the (possibly just-switched-in) viewer's view has
    // actually updated, rather than racing Angular's own change detection for the @switch block in the template.
    private _pendingResize: boolean;
    // Local-only bookkeeping for which of the two flagged Assets is older, to decide which one a third
    // flag evicts (§3.1's FIFO rule) - not synced state; the design doc's wire protocol (§3.2) has no
    // field for this, so it's a best-effort local guess, reset/refined as flags change.
    private _flagOrder: string[] = [];

    constructor(
        private websocketService: WebSocketService,
        private reviewService: ReviewService,
        private reviewVideoService: ReviewVideoService,
        private uploadService: UploadService,
        private annotationService: AnnotationService,
        private preferencesService: PreferencesService,
        private stageService: StageService,
        private matDialog: MatDialog,
        private authService: AuthService,
        private activatedRoute: ActivatedRoute,
    ) {
        this.volume = 0;
        this.muted = true;
        this.looping = true;

        this.color = '#ffffff';
        this.size = 4;
        this.tool = AnnotationTool.None;

        // Preferences
        effect(() => {
            const prefs = this.preferencesService.immediatePrefsSignal();
            const prefColor = prefs.annotation_color || '#ffffffff';
            const rgba = hexToRgb(prefColor);
            this.color = rgbToHex(rgba[0], rgba[1], rgba[2]);
            this.size = prefs.annotation_size || 4;
            this.opacity = rgba[3] || 255;
        });
    }

    ngOnInit() {
        this.syncingState$ = this.reviewService.syncingState$;
        this.presentingState$ = this.reviewService.presenterState$;
        this.annotations$ = this.annotationService.annotations$;
        this.comments$ = this.annotationService.comments$;

        // Get the review from the url
        this.activatedRoute.paramMap
            .pipe(
                take(1),
            )
            .subscribe((paramMap) => {
                let reviewId = paramMap.get('reviewId');
                if (reviewId) {
                    // Sometimes the query params will get serialized into the review id string from auth0, so we make
                    // sure to just take the guid.  Use the invalid guid constant for a guid length
                    reviewId = reviewId.substring(0, InvalidGuid.length);
                    this.reviewService.getReview(reviewId).subscribe();
                }
            });
        this.activatedRoute.queryParamMap
            .pipe(
                filter(i => Boolean(i.get('a'))),
                take(1)
            )
            .subscribe(queryParamMap => {
                this._deepLink = new DeepLink();
                this._deepLink.asset = queryParamMap.get('a');
                this._deepLink.frame = parseInt(queryParamMap.get('f'));
            })

        this.vm$ = this.reviewService.review$.pipe(
            combineLatestWith(
                this.reviewService.reviewState$.pipe(startWith(null)),
                this.websocketService.connectState$,
                this.reviewService.presenterState$,
                this.authService.user,
                // Shared single-pane chrome (bottom range buttons, annotation markers) binds to whichever
                // slot is active (§3.4) - filter out the passive slot's emissions so vm.playbackState
                // doesn't flicker between both panes' state while comparing.
                this.reviewVideoService.playbackState$.pipe(
                    filter((playbackState) => playbackState.slot === this.reviewService.getActiveSlot()),
                ),
            ),
            map(([review, reviewState, connectedState, presenterState, user, playbackState]) => {
                if (reviewState) {
                    if (reviewState.Asset) {
                        const isSyncing = this.reviewService.getLocalUser()?.IsSyncing;

                        if (!isSyncing && reviewState.Asset === InvalidGuid)
                            return { review, reviewState, connectedState, presenterState, user, playbackState };

                        let asset = review.children.find((a) => a.id == reviewState.Asset);

                        if (this._deepLink?.asset && !isSyncing) {
                            asset = review.children.find((a) => a.id == this._deepLink.asset);
                        }

                        if (asset != undefined) {
                            this.applyAsset(asset);
                        }
                        else if (review.children.length > 0) {
                            if (reviewState.Asset === InvalidGuid) {
                                this.setAsset(review.children[0], reviewState);
                            }
                            else {
                                this.applyAsset(review.children[0]);
                            }
                        }
                    }

                    if (reviewState.isComparing()) {
                        const compareAsset = review.children.find((a) => a.id == reviewState.CompareAsset);
                        if (compareAsset != undefined) {
                            this.applyCompareAsset(compareAsset);
                        }
                        else {
                            // The flagged compare Asset is no longer a child of this Review (e.g. removed
                            // via the REST side while comparing) - nothing sane to show in that pane, so
                            // drop out of compare mode rather than leaving a dangling reference.
                            this.compareAsset = null;
                        }
                    }
                    else if (this.compareAsset) {
                        this.compareAsset = null;
                    }

                    const localUser = this.reviewService.getLocalUser();
                    if (localUser) {
                        localUser.Color = this.color;
                        localUser.Size = this.size;
                    }

                    this.applyLoop(reviewState.IsLooping);
                }
                else if (review.children.length > 0) {
                    this.applyAsset(review.children[0]);
                }

                return { review, reviewState, connectedState, presenterState, user, playbackState };
            }),
        );

        // The Asset was already appended to the Review server-side by the time upload() resolves (see upload.py),
        // so broadcasting now - rather than waiting for video processing to finish - makes it show up for every
        // connected client immediately, in its not-ready state. ReviewService picks up not-ready children on
        // that refresh and subscribes for completion on behalf of every viewer, not just the uploader.
        this.uploadService.assetUploaded$.subscribe(() => {
            this.reviewService.send({}, MessageType.ReviewChange);
        });

        this._connectionStateSubscription = this.websocketService.connectState$
            .pipe(
                filter((i) => {
                    return i == ConnectedState.UserRegistered;
                }),
                combineLatestWith(this.reviewService.review$),
            )
            .subscribe((review) => {
                this.reviewService.joinReview();
            });

        // This subscription is fine since we only grab the first and it only emits once
        this.vm$
            .pipe(
                take(1),
                filter((i) => i.reviewState !== null),
            )
            .subscribe((vm) => {
                if (vm.reviewState.isActive()) {
                    const dialogData = {
                        title: 'Join Active Sync',
                        message:
                            'There is currently a synced review in progress, would you like to join?',
                        acceptLabel: 'Yes',
                        rejectLabel: 'No',
                    };
                    this.matDialog
                        .open(ConfirmDialogComponent, { data: dialogData })
                        .afterClosed()
                        .subscribe((value) => {
                            if (value) {
                                this.reviewService.requestSync(true);
                            }
                        });
                }
            });
    }

    ngOnDestroy() {
        this._connectionStateSubscription.unsubscribe();
        this.reviewService.leaveReview();
    }

    ngAfterViewChecked() {
        if (this._pendingResize) {
            this._pendingResize = false;
            // Defer past this change-detection pass: resizeHandler() mutates
            // bounds/xform state that the just-checked-in viewer's own template
            // reads, and calling it synchronously here races Angular's
            // checkNoChanges verification and throws NG0100.
            setTimeout(() => this.resizeHandler());
        }
    }

    @HostListener('window:keydown', ['$event'])
    keyDownEvent(event: KeyboardEvent) {
        this.shiftDown = event.shiftKey;
    }

    @HostListener('window:keyup', ['$event'])
    keyUpEvent(event: KeyboardEvent) {
        this.shiftDown = event.shiftKey;
    }

    public next(vm: ReviewVM): void {
        let index = vm.review.children.indexOf(this.asset);
        index += 1;
        if (index == vm.review.children.length) {
            index = 0;
        }
        this.setAsset(vm.review.children[index], vm.reviewState);
    }

    public previous(vm: ReviewVM): void {
        let index = vm.review.children.indexOf(this.asset);
        index -= 1;
        if (index < 0) {
            index = vm.review.children.length - 1;
        }
        this.setAsset(vm.review.children[index], vm.reviewState);
    }

    public requestJoin(): void {
        const activeAsset = this.activeAsset();
        if (activeAsset) {
            this.annotationService.annotationsForAsset(activeAsset.id);
        }
        this.reviewService.requestSync(true);
    }

    public requestLeave(): void {
        this.reviewService.requestSync(false);
    }

    public requestPresenter(presenterState: PresentingState): void {
        switch (presenterState) {
            case PresentingState.None:
                this.reviewService.requestPresenter(true);
                break;
            case PresentingState.Self:
                this.reviewService.requestPresenter(false);
                break;
            case PresentingState.Other:
                const dialogData = {
                    title: 'Request Presenter',
                    message:
                        'Someone is currently presenting, would you like to request to be Presenter?',
                    acceptLabel: 'Yes',
                    rejectLabel: 'Cancel',
                };
                this.matDialog
                    .open(ConfirmDialogComponent, { data: dialogData })
                    .afterClosed()
                    .subscribe((value) => {
                        if (value) {
                            this.reviewService.requestPresenter(true);
                        }
                    });
                break;
        }
    }

    public reconnect(user: User): void {
        this.reviewService.reconnect(user);
    }

    public applyAsset(asset: Asset) {
        if (this.asset && this.asset.id === asset.id) {
            return;
        }

        this.asset = asset;

        // FIXME: These should be derived from the service state
        this.muted = true;
        this.looping = true;

        // Handle deep link frame
        let frame = 0;
        if (this._deepLink?.frame && this._deepLink.isConsumed) {
            frame = this._deepLink.frame;
            this._deepLink = null;
        }
        this.reviewVideoService.setAsset(asset as VideoAsset, frame, Slot.Primary);
        // Annotations/comments are active-slot-only (§3.4) - only refetch for this slot if it's the one
        // currently driving the shared sidebar/Stage.
        if (this.reviewService.getActiveSlot() === Slot.Primary) {
            this.annotationService.annotationsForAsset(asset.id);
        }

        this._pendingResize = true;
    }

    // The Compare slot's counterpart to applyAsset() - see docs/design/review-compare-assets.md §3.2/§3.3.
    public applyCompareAsset(asset: Asset) {
        if (this.compareAsset && this.compareAsset.id === asset.id) {
            return;
        }

        this.compareAsset = asset;

        this.reviewVideoService.setAsset(asset as VideoAsset, 0, Slot.Compare);
        if (this.reviewService.getActiveSlot() === Slot.Compare) {
            this.annotationService.annotationsForAsset(asset.id);
        }

        this._pendingResize = true;
    }

    private applyLoop(value: boolean): void {
        if (value === this.looping) {
            return;
        }

        this.looping = value;
        this.reviewVideoService.loop(Source.Timeline, undefined, this.reviewService.getActiveSlot());
    }

    public setAsset(asset: Asset, reviewState: ReviewState) {
        if (reviewState.isComparing()) {
            // While comparing, the only way to change which Assets are shown is the compare flag (§3.1) -
            // flagging a third Asset evicts one of the pair (toggleCompareFlag()); a plain row click is
            // ambiguous once there's no single "current" Asset, so it's a no-op here.
            return;
        }

        reviewState.Asset = asset.id;
        reviewState.AssetType = asset.asset_type;

        reviewState.StrokeHistory = [];
        reviewState.Frame = 0;
        // PlayRange is session-global state on frog-ws (a sibling of Frame), not per-asset - switching the
        // Asset has to clear it too, or the previous asset's range stays applied for everyone still synced.
        reviewState.PlayRange = new TimeRange(null, null).toArray();
        this.stageService.resetStage();

        if (this._deepLink) {
            this._deepLink.consume();
        }

        this.applyAsset(asset);

        // Send a sparse diff, not the whole reviewState - that object can be carrying stale fields (e.g.
        // ComparePlayRange: null from an earlier full sync) that have nothing to do with this change but
        // would still get applied/broadcast if sent wholesale (see FROG-WS-39).
        const data = {
            Asset: reviewState.Asset,
            AssetType: reviewState.AssetType,
            Frame: reviewState.Frame,
            PlayRange: reviewState.PlayRange,
        };
        this.reviewService.send(data, MessageType.ReviewState);
        this.reviewService.notifyReviewState();
    }

    // Asset list row click (docs/spec/compare-ui-spec.md "Viewport panes"): while comparing, the A/B rows'
    // click makes that pane active instead of being the former no-op (there's no single "current" Asset to
    // reassign) - any other row while comparing still has no row-click affordance (flagging is the only way
    // to change the pair). Outside compare mode this is the existing single-Asset "view this" behavior.
    public selectRow(asset: Asset, reviewState: ReviewState): void {
        if (reviewState?.isComparing()) {
            if (reviewState.Asset === asset.id) {
                this.setActiveSlot(Slot.Primary, reviewState);
            } else if (reviewState.CompareAsset === asset.id) {
                this.setActiveSlot(Slot.Compare, reviewState);
            }
            return;
        }

        this.setAsset(asset, reviewState);
    }

    // Which compare slot (if any) this Asset is flagged for - drives the A/B badge vs. the plain compare-
    // flag button in the asset list, and the row's A/B coloring (§3.1, docs/spec/compare-ui-spec.md).
    public compareRole(asset: Asset, reviewState: ReviewState): CompareRole {
        if (!reviewState || !reviewState.isComparing()) {
            return null;
        }
        if (reviewState.Asset === asset.id) {
            return 'A';
        }
        if (reviewState.CompareAsset === asset.id) {
            return 'B';
        }
        return null;
    }

    // Flags/unflags an Asset for compare, or - if two are already flagged - evicts the older of the two
    // (FIFO, decided in §3.1). See docs/design/review-compare-assets.md §3.1/§3.2.
    public toggleCompareFlag(asset: Asset, review: ReviewAsset, reviewState: ReviewState): void {
        if (!reviewState) {
            return;
        }

        const isPrimary = reviewState.Asset === asset.id;
        const isCompare = reviewState.isComparing() && reviewState.CompareAsset === asset.id;

        if (isCompare) {
            // Unflag the Compare slot - drop back to the single-Asset view of whatever's in Primary.
            this._flagOrder = this._flagOrder.filter((id) => id !== asset.id);
            const data: Record<string, any> = {
                CompareAsset: InvalidGuid,
                CompareAssetType: 0,
                CompareLocked: false,
                ActiveSlot: Slot.Primary,
            };
            Object.assign(reviewState, data);
            this.compareAsset = null;
            this.reviewService.send(data, MessageType.ReviewState);
            this.reviewService.notifyReviewState();
            return;
        }

        if (isPrimary) {
            if (!reviewState.isComparing()) {
                // Nothing to fall back to - the only Asset in view can't be unflagged.
                return;
            }
            // Unflag the Primary slot: the Compare slot's Asset takes over as the sole (Primary) Asset.
            const nextAsset = review.children.find((a) => a.id === reviewState.CompareAsset);
            this._flagOrder = this._flagOrder.filter((id) => id !== asset.id);

            const data: Record<string, any> = {
                Asset: reviewState.CompareAsset,
                AssetType: reviewState.CompareAssetType,
                Frame: reviewState.CompareFrame,
                PlayRange: reviewState.ComparePlayRange,
                Transform: reviewState.CompareTransform,
                CompareAsset: InvalidGuid,
                CompareAssetType: 0,
                CompareLocked: false,
                ActiveSlot: Slot.Primary,
            };
            Object.assign(reviewState, data);
            this.stageService.resetStage();
            this.asset = null;
            this.compareAsset = null;
            if (nextAsset) {
                this.applyAsset(nextAsset);
            }
            this.reviewService.send(data, MessageType.ReviewState);
            this.reviewService.notifyReviewState();
            return;
        }

        if (!reviewState.isComparing()) {
            // First flag: pairs with whatever's currently being viewed (Primary). reviewState.Asset can
            // still be stale/InvalidGuid here for a non-syncing viewer who's never explicitly picked a row
            // (the viewport falls back to showing review.children[0] via this.asset without that ever
            // reaching reviewState - see the vm$ pipeline's isSyncing guard) - resolve from this.asset,
            // same fallback already used for assetType below, so Primary's actual id (not a stale/empty
            // one) is what gets paired, broadcast, and matched by compareRole() for the A badge/row color.
            const primaryId = this.asset?.id ?? reviewState.Asset;
            this._flagOrder = [primaryId, asset.id];
            const assetType = this.asset ? this.asset.asset_type : reviewState.AssetType;
            const data: Record<string, any> = {
                Asset: primaryId,
                AssetType: assetType,
                CompareAsset: asset.id,
                CompareAssetType: asset.asset_type,
                CompareFrame: 0,
                ComparePlayRange: new TimeRange(null, null).toArray(),
                CompareTransform: null,
                ActiveSlot: Slot.Primary,
                // Locked by default whenever eligible (same asset_type, §3.5) - the common case (comparing
                // two videos or two images) shouldn't need an extra click to get scrub/pan sync.
                CompareLocked: assetType === asset.asset_type,
            };
            Object.assign(reviewState, data);
            this.applyCompareAsset(asset);
            this.reviewService.send(data, MessageType.ReviewState);
            this.reviewService.notifyReviewState();
            return;
        }

        // Already comparing and a third Asset got flagged - evict the older of the two (FIFO, §3.1). Order
        // is a local best-effort guess (§3.2 has no synced field for it) - default to Primary being older.
        const olderId = this._flagOrder[0] ?? reviewState.Asset;
        const newerId = this._flagOrder.find((id) => id !== olderId)
            ?? (olderId === reviewState.Asset ? reviewState.CompareAsset : reviewState.Asset);
        this._flagOrder = [newerId, asset.id];

        let data: Record<string, any>;
        if (olderId === reviewState.Asset) {
            data = {
                Asset: asset.id,
                AssetType: asset.asset_type,
                Frame: 0,
                PlayRange: new TimeRange(null, null).toArray(),
                Transform: null,
            };
            if (reviewState.ActiveSlot === Slot.Primary) {
                this.stageService.resetStage();
            }
            this.asset = null;
            Object.assign(reviewState, data);
            this.applyAsset(asset);
        } else {
            data = {
                CompareAsset: asset.id,
                CompareAssetType: asset.asset_type,
                CompareFrame: 0,
                ComparePlayRange: new TimeRange(null, null).toArray(),
                CompareTransform: null,
            };
            if (reviewState.ActiveSlot === Slot.Compare) {
                this.stageService.resetStage();
            }
            this.compareAsset = null;
            Object.assign(reviewState, data);
            this.applyCompareAsset(asset);
        }

        // One side of the pair just changed Asset, so this is effectively a fresh pairing - default it to
        // locked whenever eligible (same asset_type, §3.5), same as the first flag. frog-ws independently
        // auto-unlocks on a type mismatch (it never auto-locks), but set it optimistically here too so the
        // local toggle doesn't show stale state for a beat before that broadcast comes back.
        const shouldLock = reviewState.AssetType === reviewState.CompareAssetType;
        if (reviewState.CompareLocked !== shouldLock) {
            reviewState.CompareLocked = shouldLock;
            data.CompareLocked = shouldLock;
        }

        this.reviewService.send(data, MessageType.ReviewState);
        this.reviewService.notifyReviewState();
    }

    // Only shown/enabled when both slots hold the same asset_type (§3.5).
    public toggleCompareLock(reviewState: ReviewState): void {
        if (!reviewState || !reviewState.isComparing() || reviewState.AssetType !== reviewState.CompareAssetType) {
            return;
        }

        reviewState.CompareLocked = !reviewState.CompareLocked;
        this.reviewService.send({CompareLocked: reviewState.CompareLocked}, MessageType.ReviewState);
        this.reviewService.notifyReviewState();
    }

    // Tooltip/aria-label text for the single floating lock button (docs/spec/compare-ui-spec.md "Pan/zoom
    // lock"). Kept as a TS method rather than inline in the template to avoid escaping '&'/'—' there.
    public lockTooltip(reviewState: ReviewState): string {
        return reviewState.CompareLocked
            ? 'Pan & zoom locked — click to unlink'
            : 'Pan & zoom unlinked — click to lock';
    }

    // Makes a pane active - the only way direct manipulation (timeline, pan/zoom, mute/volume) moves from
    // one pane to the other (§3.4). Pauses whichever slot is losing activity (if it's a video) rather than
    // leaving it to keep playing unsynced in the background, since nothing else will send it further Frame
    // updates once passive.
    public setActiveSlot(slot: Slot, reviewState: ReviewState): void {
        if (!reviewState || reviewState.ActiveSlot === slot) {
            return;
        }

        const data: Record<string, any> = {ActiveSlot: slot};

        // Pause the losing slot unconditionally (if it's a video) rather than gating on reviewState
        // .IsPlaying/CompareIsPlaying - those fields only reflect what's been broadcast *and echoed back*,
        // and ReviewState updates are never echoed back to whoever sent them (see ReviewService.send()), so
        // they can't be trusted to reflect this client's own prior play action. Pausing an already-paused
        // video is harmless.
        const losingSlot = reviewState.ActiveSlot;
        const losingSlotIsVideo = losingSlot === Slot.Primary
            ? reviewState.AssetType === AssetType.Video
            : reviewState.CompareAssetType === AssetType.Video;

        if (losingSlotIsVideo) {
            if (losingSlot === Slot.Primary) {
                data.IsPlaying = false;
                reviewState.IsPlaying = false;
            } else {
                data.CompareIsPlaying = false;
                reviewState.CompareIsPlaying = false;
            }
            this.reviewVideoService.play(Source.All, false, losingSlot);
        }

        reviewState.ActiveSlot = slot;
        // No resize() sweep here (unlike applyAsset()/applyCompareAsset(), which still need one) - neither
        // pane actually changes size when the active one flips (docs/spec/compare-ui-spec.md just dims the
        // inactive header), and resize() unconditionally re-fits images to center/default zoom, which was
        // wiping out any pan/zoom the user had set on whichever pane just went passive.
        this.reviewService.send(data, MessageType.ReviewState);
        this.reviewService.notifyReviewState();

        const activeAsset = slot === Slot.Compare ? this.compareAsset : this.asset;
        if (activeAsset) {
            this.annotationService.annotationsForAsset(activeAsset.id);
        }
    }

    public resizeHandler(): void {
        this.viewers.forEach((viewer) => viewer.resize());
    }

    public muteChange() {
        this.muted = !this.muted;
        this.reviewVideoService.mute(Source.Timeline, undefined, this.reviewService.getActiveSlot());
        if (this.muted) {
            this.volume = 0;
            this.reviewVideoService.setVolume(0, this.reviewService.getActiveSlot());
        }
    }

    public volumeChange() {
        this.reviewVideoService.setVolume(this.volume / 100.0, this.reviewService.getActiveSlot());
        this.muted = this.volume === 0;
        this.reviewVideoService.mute(Source.Timeline, this.muted, this.reviewService.getActiveSlot());
    }

    public loopChange() {
        this.looping = !this.looping;
        this.reviewVideoService.loop(Source.Timeline, undefined, this.reviewService.getActiveSlot());

        const data = {
            IsLooping: this.looping,
        };
        this.reviewService.send(data, MessageType.ReviewState);
    }

    // Whichever Asset the shared single-pane chrome (timeline, annotation markers, range buttons) should
    // currently act on - the active slot's Asset (§3.4).
    public activeAsset(): Asset {
        return this.reviewService.getActiveSlot() === Slot.Compare ? this.compareAsset : this.asset;
    }

    public nextAnnotationMarker(currentFrame: number, annotations: Annotation[]) {
        const videoAsset = deserializeAsset(this.activeAsset()) as VideoAsset;
        const currentTime = videoAsset.frameToTime(currentFrame);
        const timestamps = annotations.filter((i) => i.timestamp >= 0).map((i) => i.timestamp);
        timestamps.sort((a, b) => a - b);

        let nextTimestamp = firstOrDefault(timestamps, (i) => i > currentTime);
        if (nextTimestamp === null) {
            nextTimestamp = timestamps[0];
        }

        const frame = videoAsset.timeToFrame(nextTimestamp);
        this.reviewVideoService.setFrame(frame, Source.All, this.reviewService.getActiveSlot());

        this.notifyFrame(frame);
    }

    public previousAnnotationMarker(currentFrame: number, annotations: Annotation[]) {
        const videoAsset = deserializeAsset(this.activeAsset()) as VideoAsset;
        const currentTime = videoAsset.frameToTime(currentFrame);
        const timestamps = annotations.filter((i) => i.timestamp >= 0).map((i) => i.timestamp);
        timestamps.sort((a, b) => a - b);
        timestamps.reverse();

        let nextTimestamp = firstOrDefault(timestamps, (i) => i < currentTime);
        if (nextTimestamp === null) {
            nextTimestamp = timestamps[0];
        }

        const frame = videoAsset.timeToFrame(nextTimestamp);
        this.reviewVideoService.setFrame(frame, Source.All, this.reviewService.getActiveSlot());

        this.notifyFrame(frame);
    }

    public setRangeStart(playbackState: PlaybackState): void {
        const timeScale = new TimeScale(playbackState.frameCount, playbackState.frameRate);
        const startTime = timeScale.frameToTime(playbackState.frame);

        const timeRange = playbackState.playRange;
        timeRange.start = timeRange.start === startTime ? null : startTime;
        this.reviewVideoService.setRange(timeRange.toArray(), Source.All, this.reviewService.getActiveSlot());
        const data = {
            PlayRange: timeRange.toArray(),
        };
        this.reviewService.sendActive(data);
    }

    public setRangeEnd(playbackState: PlaybackState): void {
        const timeScale = new TimeScale(playbackState.frameCount, playbackState.frameRate);
        const endTime = timeScale.frameToTime(playbackState.frame);

        const timeRange = playbackState.playRange;
        timeRange.end = timeRange.end === endTime ? null : endTime;
        this.reviewVideoService.setRange(timeRange.toArray(), Source.All, this.reviewService.getActiveSlot());

        const data = {
            PlayRange: timeRange.toArray(),
        };
        this.reviewService.sendActive(data);
    }

    private notifyFrame(frame: number): void {
        const data = {
            Frame: frame,
        };
        this.reviewService.sendActive(data);
    }

    setUserColor() {
        const localUser = this.reviewService.getLocalUser();
        const opacityHex = intToHex(this.opacity);
        const rgba = `${this.color}${opacityHex}`;
        const size = this.size;
        localUser.Color = rgba;
        localUser.Size = size;
        this.preferencesService.sparseUpdate("annotation_color", rgba);
        this.preferencesService.sparseUpdate("annotation_size", size);
    }
}
